import { type PAGINATION_PARAM_KEYS } from '@/router/searchParams/defaultPaginationParams';
import { type USER_SORT_KEYS } from '@/router/searchParams/userSearchParams';
import { type NavigableRouteType, type RouteSearchParamEnumType } from '@/router/types';

import { instance } from './appRoutes';

export const users = {
  path: `${instance.path}/users`,
  description:
    'A list of users for the company. Each user is shown with their name, email, role, and status. New users can also be invited from this page.',
  navigable: true,
  pathParams: {},
  searchParams: {
    'u.page': {
      type: 'string',
      description: 'The page number to display',
    },
    'u.size': {
      type: 'string',
      description: 'The number of items to display per page',
    },
    'u.sortBy': {
      type: 'enum',
      enumValues: ['email', 'name', 'role', 'status'],
      default: 'name',
      description: 'The field to sort by',
    } satisfies RouteSearchParamEnumType<USER_SORT_KEYS>,
    'u.sortOrder': {
      type: 'enum',
      enumValues: ['asc', 'desc'],
      default: 'asc',
      description: 'The sort order',
    },
    'u.search': {
      type: 'string',
      description: 'A string to search for in the user name, email, or role',
    },
  },
} satisfies NavigableRouteType<PAGINATION_PARAM_KEYS<'u'>>;

export const userInvite = {
  path: `${instance.path}/users/invite`,
  description:
    'A form where an admin can fill out the name, email and desired permission level for a new user (user or admin).',
  navigable: true,
  pathParams: {},
  searchParams: {},
} satisfies NavigableRouteType;

export const userDetail = {
  path: `${instance.path}/users/:user`,
  description:
    "A page where an admin can view and edit a user's name, email, and permission level. The admin can also delete the user from Channel99 here.",
  navigable: true,
  pathParams: {
    user: {
      type: 'string',
      description: 'The ID of the user the user wishes to view',
      requiresLookup: true,
      lookupType: 'user',
      required: true,
    },
  },
  searchParams: {},
} satisfies NavigableRouteType;

export const profile = {
  path: `${instance.path}/profile`,
  description:
    "The logged-in user's profile page. The user can change their name or log out from this page. They can also manage emails (notifications) they receive for reports and goals.",
  navigable: true,
  pathParams: {},
  searchParams: {},
} satisfies NavigableRouteType;

export const loginAs = {
  path: `${instance.path}/profile/login-as`,
  navigable: false,
  pathParams: {},
  searchParams: {},
};

export const userById = (userId?: string) => `${users.path}/${userId}`;
